/*
 * Copyright (c) 2023 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { LocaleConstants } from '../utils/locale-constants';
import * as moment from 'moment';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

//TODO rename to iriro-date-range-component
@Component({
  selector: 'iric-date-range-component, iriro-date-range',
  templateUrl: './date-range.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangeComponent),
      multi: true,
    }
  ],
})
export class DateRangeComponent implements OnInit, ControlValueAccessor {
  @Input() displayDateFormat = 'yy/mm/dd';
  @Input() datePattern = 'YYYY-MM-DD';
  @Input() yearRange: string | undefined; // 1900:2030
  @Input() suffix = '';
  @Input() class: string | undefined;
  @Input() calendarStyle: { [key: string]: string } | undefined;
  @Input() appendTo = 'body';
  @Input() showButtonBar = false;
  @Input() showClear = true;
  @Input() disabled = false;
  @Input() invalid = false;
  @Input() dirty = false;
  @Input() minimumDay: Date | null = null;
  @Input() maximumDay: Date | null = null;
  @Output() dateSelected = new EventEmitter<any>();

  locale: any | undefined = undefined;
  date: MinMax | undefined;
  minDate: Date | undefined | null;
  maxDate: Date | undefined | null;

  private _onChange: any | undefined = undefined;
  private _onTouched: any | undefined = undefined;

  constructor(private readonly translateService: TranslateService) {
    this.initLocale();
  }

  _startDate: Date | null = null;

  get startDate(): Date | null {
    return this._startDate;
  }

  @Input() set startDate(date: Date | null) {
    this._startDate = date;
    if (this.date) this.date.startDate = date;
  }

  _endDate: Date | null = null;

  get endDate(): Date | null {
    return this._endDate;
  }

  @Input() set endDate(date: Date | null) {
    this._endDate = date;
    if (this.date) this.date.endDate = date;
  }

  ngOnInit() {
    this.datePattern = this.datePattern ? this.datePattern : 'YYYY-MM-DDTHH:mm:SS';

    if (this.yearRange) {
      this.minDate = this.startDate ? this.startDate : new Date(this.yearRange.split(':')[0]);
      this.maxDate = this.endDate ? this.endDate : new Date(this.yearRange.split(':')[1]);
    } else {
      this.minDate = this.startDate ? this.startDate : null;
      this.maxDate = this.endDate ? this.endDate : null;
    }

    this.date = {
      startDate: this.minDate,
      endDate: this.maxDate,
    };
  }

  onChangeStart(newStartDate: Date) {
    this.startDate = newStartDate ? newStartDate : null;
    this.emitDateSelected();
  }

  onChangeEnd(newEndDate: Date) {
    this.endDate = newEndDate ? newEndDate : null;
    this.emitDateSelected();
  }

  onClear(type: string) {
    if (type === 'start') {
      this.startDate = null;
    } else {
      this.endDate = null;
    }
    this.emitDateSelected();
  }

  emitDateSelected() {
    const formattedStartDate = this.startDate ? moment(this.startDate).format(this.datePattern) + this.suffix : null;
    const formattedEndDate = this.endDate ? moment(this.endDate).format(this.datePattern) + this.suffix : null;
    if (formattedStartDate === null && formattedEndDate === null) {
      this.dateSelected.emit(null);
      if (this._onChange) {
        this._onChange(null);
      }
    } else {
      this.dateSelected.emit({ startDate: formattedStartDate, endDate: formattedEndDate });
      if (this._onChange) {
        this._onChange({ startDate: formattedStartDate, endDate: formattedEndDate });
      }
    }
  }

  initLocale() {
    this.locale = LocaleConstants[this.translateService.currentLang];
    this.translateService.onLangChange.subscribe((params: LangChangeEvent) => {
      this.locale = LocaleConstants[params.lang]
        ? LocaleConstants[params.lang]
        : LocaleConstants[this.translateService.defaultLang];
    });
  }

  registerOnChange(fn: any): void {
    console.log('registerOnChange');
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  writeValue(value: any): void {
    if (value) {
      this.startDate = value.startDate;
      this.endDate = value.endDate;
    } else {
      this.startDate = null;
      this.endDate = null;
    }
    this._onChange({ startDate: this.startDate, endDate: this.endDate });
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}

export interface MinMax {
  startDate: any | undefined;
  endDate: any | undefined;
}
